import { CommonModule } from '@angular/common';
import { Component, OnInit, input, output, inject } from '@angular/core';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { FileUploadModule } from 'primeng/fileupload';

import { HeadingComponent } from '../../../../shared/components/heading/heading.component';
import { DynamicCredentialService } from '../../../../shared/services/dynamic-credential.service';
import { ReviewerService } from '../../../../shared/services/reviewer.service';
import { lastValueFrom } from 'rxjs';
import { Reviewer } from '../../../../store/reviewer/reviewer.interface';
import { DocumentService } from '../../../../shared/services/document-service';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { FirmCredential, Id, ReviewerCredential } from './credentials.model';
import { EmittersService } from '../../../../shared/services/emitters.service';
import { Router } from '@angular/router';
import { HelperService } from '../../../../shared/services/helper.service';
import { PipesModule } from '../../../../shared/pipes/pipes.module';
import { CommonBaseClass } from '../../../../shared/classes/common-base-class';
import { StorageService } from '../../../../shared/services/storage.service';
import { environment } from '../../../../../environments/environment';
import { UserService } from '../../../../shared/services/user.service';

interface UploadEvent {
  originalEvent: Event;
  files: File[];
}

@Component({
  selector: 'app-credentials',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    FloatLabelModule,
    CalendarModule,
    InputTextModule,
    DropdownModule,
    HeadingComponent,
    MultiSelectModule,
    AccordionModule,
    TagModule,
    FileUploadModule,
    ChipModule,
    PipesModule
  ],
  templateUrl: './credentials.component.html',
  styleUrl: './credentials.component.scss',
})
export class CredentialsComponent extends CommonBaseClass implements OnInit {

  /**
   * MP-5736: the CHILD firm whose view of this reviewer we are editing, or ''
   * when there is none - which is every reviewer, every standalone firm and,
   * until the MP-5643 switcher lands, every parent-level FM. Sending '' means
   * "today's path", so this call is a no-op for all of them.
   */
  private readonly actingFirmUserService = inject(UserService);
  from = input<string>('');
  nextStep = output<void>();
  backStep = output<void>();

  userFirmId: string = '';
  userFirmName: string = '';
  appName: string = environment.title;
  dynamicCredentials!: Id[];
  headingButton: { url: string; label: string; colorClass: string; icon: string; queryParams?: Record<string, string> } = {
    url: '/reviewer/profile/credentials/create',
    label: 'Add credentials',
    colorClass: 'button-bg-pink',
    icon: 'add'
  };
  reviewer!: Reviewer;
  reviewerCredentials!: ReviewerCredential[];
  secondaryOptions!: string[];
  breadcrumbTitleOptions = [
    { label: 'Profile' }, 
    { label: 'Credentials' }
  ];

  constructor(
    private dynamicCredentialService: DynamicCredentialService,
    private reviewerService: ReviewerService,
    private documentService: DocumentService,
    private confirmationDialogService: ConfirmationDialogService,
    private toastService: ToastService,
    private emitterService: EmittersService,
    private storageService: StorageService,
    private router: Router,
    helperService: HelperService
  ) {
    super(helperService);
    this.emitterService.tenantEmitter.subscribe((event)=>{
      this.ngOnInit();
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.from() === 'onboarding') {
      this.headingButton = { ...this.headingButton, url: '/onboarding/credentials/create', queryParams: { from: 'onboarding' } };
    }
    this.userFirmId = this.reviewerService.getCurrentTenant();
    this.userFirmName = this.storageService.getTenant()?.firm?.name || '';
    await this.getReviewerInfo();
    await this.getDynamicCredentials();
    await this.getReviewerCredentials();
    this.parseSecondaryOptions();
    this.appendDownloadUrlArray();
  }

  async getDynamicCredentials(): Promise<void> {
    this.dynamicCredentials = await lastValueFrom(
      this.reviewerService.getFilteredCredentialBasedUserSkills(this.reviewer._id)
    );
  }

  async getReviewerCredentials(): Promise<void> {
    this.reviewerCredentials = await lastValueFrom(
      this.dynamicCredentialService.getReviewerCredentials(this.reviewer._id, this.actingFirmUserService.getActingChildFirmId())
    );
  }

  async getReviewerInfo(): Promise<void> {
    this.reviewer = await lastValueFrom(this.reviewerService.fetchReviewer());
  }

  parseSecondaryOptions(): void {
    this.reviewerCredentials.forEach((cred: ReviewerCredential) => {
      if (cred.secondaryOptions && cred.secondaryOptions.trim() !== '') {
        cred.secondaryOptionsArray = cred.secondaryOptions.split(',');
      }
    });
  }

  appendDownloadUrlArray(): void {
    this.reviewerCredentials.forEach((cred: ReviewerCredential) => {
      if (cred.certificate) {
        const downloadURL = this.documentService.getDocumentPathById(
          cred.certificate._id
        );
        if (downloadURL) {
          cred.certificate.downloadURL = downloadURL;
        }
      }
    });
  }

  updateReviewerCredentials(reviewerCreds: ReviewerCredential[], deleteKey: boolean = false): void {
    this.dynamicCredentialService
      .updateReviewerDynamicCredentials({
        reviewerId: this.reviewer._id,
        credentials: reviewerCreds,
        deleteKey: deleteKey,
        // MP-5736: routes the write to this child's own records (MP-5734).
        actingFirmId: this.actingFirmUserService.getActingChildFirmId()
      })
      .subscribe({
        next: (res) => {
          this.toastService.showSuccess('Credentials updated');
          this.ngOnInit();
        },
      });
  }

  editMode(event: any, creds: ReviewerCredential): void {
    // MP-5739: the record _id is unique (MP-5737), so it alone identifies the
    // record to edit — no secondaryOptions disambiguation parameter anymore
    const extras = this.from() === 'onboarding' ? { queryParams: { from: 'onboarding' } } : {};
    const path = this.from() === 'onboarding'
      ? `/onboarding/credentials/edit/${creds._id}`
      : `/reviewer/profile/credentials/edit/${creds._id}`;
    this.router.navigate([path], extras);
  }

  onDelete($event: Event, cred: ReviewerCredential): void{
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message:
        'You are about to delete the credentials of ' +
        cred?.name +
        '. Are you sure?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        // MP-5739: delete exactly this record by its _id — deleting by name
        // removed every record of the credential (e.g. both Bar Membership
        // states). The deleteKey payload is unchanged.
        const reviewerCreds = this.reviewerCredentials.filter(
          (credential: ReviewerCredential) => credential._id !== cred._id
        );
        reviewerCreds.forEach((obj: ReviewerCredential) => {
          if (obj.secondaryOptionsArray) {
            delete obj.secondaryOptionsArray;
          }
        });
        this.updateReviewerCredentials(reviewerCreds, true);
      },
    });
  }

  onUpload(event: any) {
    // this.messageService.add({ severity: 'info', summary: 'Success', detail: 'File Uploaded with Basic Mode' });
  }

  addCredentials(): void {
    const extras = this.from() === 'onboarding' ? { queryParams: { from: 'onboarding' } } : {};
    const path = this.from() === 'onboarding' ? '/onboarding/credentials/create' : '/reviewer/profile/credentials/create';
    this.router.navigate([path], extras);
  }

  onNext(): void {
    this.nextStep.emit();
  }

onBack(): void {
    if (this.from() === 'onboarding') {
      this.backStep.emit();
    } else {
      this.navigateToMainProfile();
    }
  }
}
